import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Sparkles, Upload, X, Save, Crop } from 'lucide-react';
import type { Area } from 'react-easy-crop';
import { Banner, BannerFormData } from '@/types/banner';
import { aiApi } from '@/services/aiApi';
import { bannerApi } from '@/services/bannerApi';
import { uploadImageToStorageV2 } from '@/utils/storage';
import { getCroppedImageFile } from '@/utils/cropImage';
import { taxonomyApi } from '@/services/taxonomyApi';
import { ImageCropModal } from '@/components/ImageCropModal';
import { BANNER_ASPECT_RATIO, getEffectivePlacement } from '@shared/bannerAspectRatio';
import toast from 'react-hot-toast';

const placementAspectInfo: Record<NonNullable<BannerFormData['placement']>, { ratio: number; label: string; previewClass: string }> = {
  hero: { ratio: BANNER_ASPECT_RATIO.hero, label: 'Hero Slider (21:9)', previewClass: 'aspect-[21/9]' },
  featured: { ratio: BANNER_ASPECT_RATIO.featured, label: 'Vitrin Büyük (4:3)', previewClass: 'aspect-[4/3]' },
  small: { ratio: BANNER_ASPECT_RATIO.small, label: 'Vitrin Küçük (1:1)', previewClass: 'aspect-square' },
};

interface Category {
  id: string;
  name: string;
  slug?: string;
  parentId?: string;
}

const defaultFormData: BannerFormData = {
  imageUrl: '',
  title: '',
  subtitle: '',
  buttonText: '',
  textColor: '#FFFFFF',
  textPosition: 'center',
  linkType: 'none',
  linkValue: '',
  gridSize: 'medium',
  placement: 'featured',
  order: 0,
  isActive: true,
  overlayOpacity: 30,
};

export const BannerForm: React.FC = () => {
  const navigate = useNavigate();
  const { bannerId } = useParams<{ bannerId: string }>();
  const isEditMode = Boolean(bannerId);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<BannerFormData>(defaultFormData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [showTextFields, setShowTextFields] = useState(false);
  const [cropModalState, setCropModalState] = useState<{ imageSrc: string; fileName: string } | null>(null);

  // Load existing banner and categories
  useEffect(() => {
    const init = async () => {
      // Load categories for link selection
      try {
        const cats = await taxonomyApi.getCategories();
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (error) {
        console.error('Kategoriler yüklenemedi:', error);
      }

      // Load banner if editing
      if (bannerId) {
        try {
          setLoading(true);
          const banner = await bannerApi.getBanner(bannerId);
          setFormData({
            imageUrl: banner.imageUrl || '',
            title: banner.title || '',
            subtitle: banner.subtitle || '',
            buttonText: banner.buttonText || '',
            textColor: banner.textColor || '#FFFFFF',
            textPosition: banner.textPosition || 'center',
            linkType: banner.linkType || 'none',
            linkValue: banner.linkValue || '',
            gridSize: banner.gridSize || 'medium',
            placement: banner.placement
              || (banner.gridSize === 'large' ? 'featured' : 'small'),
            order: banner.order || 0,
            isActive: banner.isActive !== false,
            overlayOpacity: banner.overlayOpacity ?? 30,
          });
          setShowTextFields(!!(banner.title || banner.subtitle || banner.buttonText));
        } catch (error: any) {
          toast.error(error.message || 'Banner yüklenemedi');
          navigate('/admin/banners');
        } finally {
          setLoading(false);
        }
      }
    };
    init();
  }, [bannerId, navigate]);

  const handleChange = (field: keyof BannerFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const processSelectedFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir görsel dosyası seçin');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setCropModalState({ imageSrc: objectUrl, fileName: file.name });
  };

  const closeCropModal = () => {
    if (cropModalState) URL.revokeObjectURL(cropModalState.imageSrc);
    setCropModalState(null);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processSelectedFile(file);
    e.target.value = '';
  };

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processSelectedFile(file);
  };

  const handleRecrop = async () => {
    if (!formData.imageUrl) return;
    try {
      const blob = await fetch(formData.imageUrl).then((r) => r.blob());
      const file = new File([blob], 'banner.jpg', { type: blob.type || 'image/jpeg' });
      processSelectedFile(file);
    } catch (error) {
      console.error('Recrop fetch error:', error);
      toast.error('Görsel yeniden kırpma için yüklenemedi');
    }
  };

  const handleCropConfirm = async (croppedAreaPixels: Area) => {
    if (!cropModalState) return;
    try {
      setUploading(true);
      const croppedFile = await getCroppedImageFile(
        cropModalState.imageSrc,
        croppedAreaPixels,
        cropModalState.fileName
      );
      const url = await uploadImageToStorageV2(croppedFile, 'banners', 'banner');
      handleChange('imageUrl', url);
      toast.success('Görsel kırpıldı ve yüklendi!');
      closeCropModal();
    } catch (error) {
      console.error('Crop/upload error:', error);
      toast.error('Görsel yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateImage = async () => {
    const prompt = imagePrompt.trim();

    if (!prompt) {
      toast.error('Önce üretilecek banner görselini kısaca tarif edin');
      return;
    }

    try {
      setGeneratingImage(true);
      const result = await aiApi.generateBannerImage({
        prompt,
        title: formData.title,
        subtitle: formData.subtitle,
        buttonText: formData.buttonText,
        gridSize: formData.gridSize,
        referenceImageUrl: formData.imageUrl || undefined,
      });

      const blob = await fetch(result.url).then((r) => r.blob());
      const file = new File([blob], 'banner-ai.jpg', { type: blob.type || 'image/jpeg' });
      processSelectedFile(file);
      toast.success('Banner görseli üretildi, şimdi kırpın');
    } catch (error: any) {
      console.error('Banner image generation error:', error);
      toast.error(error.response?.data?.error || error.message || 'Banner görseli üretilemedi');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.imageUrl) {
      toast.error('Banner görseli zorunludur');
      return;
    }

    // Clear text fields if toggle is off
    const submitData = { ...formData };
    if (!showTextFields) {
      submitData.title = '';
      submitData.subtitle = '';
      submitData.buttonText = '';
    }

    try {
      setSaving(true);
      if (isEditMode && bannerId) {
        await bannerApi.updateBanner(bannerId, submitData);
        toast.success('Banner güncellendi!');
      } else {
        await bannerApi.createBanner(submitData);
        toast.success('Banner oluşturuldu!');
      }
      navigate('/admin/banners');
    } catch (error: any) {
      toast.error(error.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/banners')}
          className="p-2 hover:bg-gray-100 rounded-lg transition"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? '🖼️ Banner Düzenle' : '🖼️ Yeni Banner'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Form Fields */}
        <div className="space-y-6">
          {/* Image Upload */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h2 className="text-lg font-semibold mb-1">Görsel</h2>
            <p className="text-xs text-gray-500 mb-4">
              Seçtiğiniz "{placementAspectInfo[getEffectivePlacement(formData)].label}" yerleşimi için görsel
              yüklerken kırpma ekranı bu orana kilitlenecek.
            </p>
            <div
              className={`
                relative border-2 border-dashed rounded-xl p-8 text-center transition
                ${uploading ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
              `}
              onDragOver={(e) => e.preventDefault()}
              onDrop={handleImageDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{ cursor: 'pointer' }}
            >
              {uploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
                  <p className="text-sm text-blue-600">Yükleniyor...</p>
                </div>
              ) : formData.imageUrl ? (
                <div className="relative">
                  <img
                    src={formData.imageUrl}
                    alt="Banner preview"
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <div className="absolute top-2 right-2 flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRecrop();
                      }}
                      className="p-1 bg-blue-500 text-white rounded-full hover:bg-blue-600"
                      title="Yeniden kırp"
                    >
                      <Crop size={16} />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleChange('imageUrl', '');
                      }}
                      className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 text-gray-400">
                  <Upload size={32} />
                  <p className="text-sm">Görsel sürükleyin veya tıklayın</p>
                  <p className="text-xs">PNG, JPG, WebP (maks. 5MB)</p>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
            </div>

            <div className="mt-5 border-t border-gray-100 pt-5 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Gemini ile üret</h3>
                  <p className="text-xs text-gray-500 mt-1">
                    Varsa yüklediğiniz görseli referans alarak banner versiyonu üretir. Yoksa brief'e göre sıfırdan üretir.
                  </p>
                </div>
                <div className="rounded-full bg-amber-50 p-2 text-amber-600">
                  <Sparkles size={16} />
                </div>
              </div>

              <textarea
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                rows={5}
                placeholder="Örn: Açık pembe ve beyaz güllerden oluşan premium bir bahar buketi, yumuşak gün ışığı, modern stüdyo hissi, sağ tarafta metin için boş alan bırak"
                className="w-full resize-y px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />

              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                  İpucu: Yüklediğiniz görsel varsa onu bağlam olarak kullanır; brief içinde stil, atmosfer ve metin boşluğu ihtiyacını belirtin.
                </p>
                <button
                  type="button"
                  onClick={handleGenerateImage}
                  disabled={generatingImage || uploading || !imagePrompt.trim()}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Sparkles size={16} />
                  {generatingImage ? 'Üretiliyor...' : 'Görsel Üret'}
                </button>
              </div>
            </div>
          </div>

          {/* Text Content Toggle */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Yazı İçeriği</h2>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showTextFields}
                  onChange={(e) => setShowTextFields(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                <span className="ml-3 text-sm text-gray-600">
                  {showTextFields ? 'Yazı göster' : 'Sadece görsel'}
                </span>
              </label>
            </div>

            {showTextFields && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Başlık</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    placeholder="Banner başlığı"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Alt Yazı</label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => handleChange('subtitle', e.target.value)}
                    placeholder="Alt açıklama"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Buton Metni</label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => handleChange('buttonText', e.target.value)}
                    placeholder="Örn: Alışverişe Başla"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Text Style */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yazı Rengi</label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.textColor}
                        onChange={(e) => handleChange('textColor', e.target.value)}
                        className="w-10 h-10 border rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.textColor}
                        onChange={(e) => handleChange('textColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Yazı Konumu</label>
                    <select
                      value={formData.textPosition}
                      onChange={(e) => handleChange('textPosition', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="left">Sol</option>
                      <option value="center">Orta</option>
                      <option value="right">Sağ</option>
                    </select>
                  </div>
                </div>

                {/* Overlay */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Arka Plan Karartma: %{formData.overlayOpacity}
                  </label>
                  <input
                    type="range"
                    min={0}
                    max={80}
                    value={formData.overlayOpacity}
                    onChange={(e) => handleChange('overlayOpacity', Number(e.target.value))}
                    className="w-full accent-blue-600"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Link Settings */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Tıklama Ayarları</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Link Tipi</label>
                <select
                  value={formData.linkType}
                  onChange={(e) => {
                    handleChange('linkType', e.target.value);
                    handleChange('linkValue', '');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="none">Tıklanabilir değil</option>
                  <option value="category">Kategori sayfası</option>
                  <option value="product">Ürün sayfası</option>
                  <option value="page">Dahili sayfa</option>
                  <option value="url">Harici URL</option>
                </select>
              </div>

              {formData.linkType === 'category' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategori Seçin</label>
                  <select
                    value={formData.linkValue}
                    onChange={(e) => handleChange('linkValue', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Kategori seçin...</option>
                    {categories
                      .filter(c => !c.parentId)
                      .map(cat => (
                        <optgroup key={cat.id} label={cat.name}>
                          <option value={cat.slug || cat.id}>{cat.name}</option>
                          {categories
                            .filter(child => child.parentId === cat.id)
                            .map(child => (
                              <option key={child.id} value={child.slug || child.id}>
                                ↳ {child.name}
                              </option>
                            ))}
                        </optgroup>
                      ))}
                  </select>
                </div>
              )}

              {formData.linkType === 'product' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ürün Slug</label>
                  <input
                    type="text"
                    value={formData.linkValue}
                    onChange={(e) => handleChange('linkValue', e.target.value)}
                    placeholder="urun-slug-adi"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {formData.linkType === 'page' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sayfa Yolu</label>
                  <input
                    type="text"
                    value={formData.linkValue}
                    onChange={(e) => handleChange('linkValue', e.target.value)}
                    placeholder="/kampanyalar"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}

              {formData.linkType === 'url' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
                  <input
                    type="url"
                    value={formData.linkValue}
                    onChange={(e) => handleChange('linkValue', e.target.value)}
                    placeholder="https://ornek.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Grid & Status */}
          <div className="bg-white p-6 rounded-xl border shadow-sm">
            <h2 className="text-lg font-semibold mb-4">Görünüm Ayarları</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yerleşim Yeri</label>
                <div className="grid grid-cols-3 gap-3">
                  {([
                    { value: 'hero', label: 'Hero Slider', desc: 'En üst büyük dönen banner', defaultGrid: 'large' as const },
                    { value: 'featured', label: 'Vitrin Büyük', desc: '2 sütun öne çıkan', defaultGrid: 'large' as const },
                    { value: 'small', label: 'Vitrin Küçük', desc: '3 sütun küçük kart', defaultGrid: 'small' as const },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        const placementChanged = formData.placement !== opt.value;
                        handleChange('placement', opt.value);
                        // Placement değişince mantıklı default gridSize öner (kullanıcı sonradan değiştirebilir)
                        handleChange('gridSize', opt.defaultGrid);
                        if (placementChanged && formData.imageUrl) {
                          toast('Yerleşim değişti — görsel yeni orana göre kırpılmamış olabilir. "Yeniden kırp" ile düzeltebilirsiniz.', { icon: '⚠️', duration: 5000 });
                        }
                      }}
                      className={`p-3 border-2 rounded-xl text-left transition ${
                        formData.placement === opt.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <span className="block text-sm font-semibold">{opt.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Web ana sayfasında banner'ın hangi bölümde gösterileceğini belirler.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Grid Boyutu</label>
                <div className="grid grid-cols-3 gap-3">
                  {(['large', 'medium', 'small'] as const).map((size) => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => handleChange('gridSize', size)}
                      className={`p-3 border-2 rounded-xl text-center transition ${
                        formData.gridSize === size
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="mb-1">
                        {size === 'large' && (
                          <div className="w-12 h-10 bg-current opacity-20 rounded mx-auto" />
                        )}
                        {size === 'medium' && (
                          <div className="w-10 h-6 bg-current opacity-20 rounded mx-auto" />
                        )}
                        {size === 'small' && (
                          <div className="w-6 h-6 bg-current opacity-20 rounded mx-auto" />
                        )}
                      </div>
                      <span className="text-sm font-medium">
                        {size === 'large' ? 'Büyük' : size === 'medium' ? 'Orta' : 'Küçük'}
                      </span>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {size === 'large'
                          ? '2 Sütun × 2 Satır'
                          : size === 'medium'
                          ? '2 Sütun × 1 Satır'
                          : '1 Sütun × 1 Satır'}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={saving || generatingImage || !formData.imageUrl}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save size={20} />
            {saving
              ? 'Kaydediliyor...'
              : isEditMode
              ? 'Güncelle'
              : 'Kaydet'}
          </button>
        </div>

        {/* Right Column - Live Preview */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-xl border shadow-sm sticky top-6">
            <h2 className="text-lg font-semibold mb-4">Canlı Önizleme</h2>
            <div
              className={`
                relative overflow-hidden rounded-xl
                ${placementAspectInfo[getEffectivePlacement(formData)].previewClass}
              `}
              style={{ minHeight: 200 }}
            >
              {/* Background */}
              {formData.imageUrl ? (
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{ backgroundImage: `url(${formData.imageUrl})` }}
                />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <span className="text-gray-400">Görsel yok</span>
                </div>
              )}

              {/* Overlay */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundColor: `rgba(0, 0, 0, ${(formData.overlayOpacity || 0) / 100})`,
                }}
              />

              {/* Text */}
              {showTextFields && (formData.title || formData.subtitle || formData.buttonText) && (
                <div
                  className={`
                    relative z-10 flex flex-col justify-center h-full p-6
                    ${formData.textPosition === 'left'
                      ? 'items-start text-left'
                      : formData.textPosition === 'right'
                      ? 'items-end text-right'
                      : 'items-center text-center'}
                  `}
                >
                  {formData.title && (
                    <h3 className="text-2xl font-bold mb-1" style={{ color: formData.textColor }}>
                      {formData.title}
                    </h3>
                  )}
                  {formData.subtitle && (
                    <p className="text-base opacity-90 mb-3" style={{ color: formData.textColor }}>
                      {formData.subtitle}
                    </p>
                  )}
                  {formData.buttonText && (
                    <span
                      className="inline-block px-5 py-2 rounded-full text-sm font-semibold bg-white/20 backdrop-blur-sm border border-white/30"
                      style={{ color: formData.textColor }}
                    >
                      {formData.buttonText}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Grid Visual */}
            <div className="mt-6">
              <p className="text-sm text-gray-500 mb-2">Grid'deki konumu:</p>
              <div className="grid grid-cols-4 gap-1 h-20">
                {formData.gridSize === 'large' && (
                  <>
                    <div className="col-span-2 row-span-2 bg-blue-200 rounded border-2 border-blue-400 flex items-center justify-center text-xs text-blue-600 font-medium">
                      Bu Banner
                    </div>
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                  </>
                )}
                {formData.gridSize === 'medium' && (
                  <>
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="col-span-2 bg-blue-200 rounded border-2 border-blue-400 flex items-center justify-center text-xs text-blue-600 font-medium">
                      Bu Banner
                    </div>
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                  </>
                )}
                {formData.gridSize === 'small' && (
                  <>
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-blue-200 rounded border-2 border-blue-400 flex items-center justify-center text-xs text-blue-600 font-medium">
                      Bu
                    </div>
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                    <div className="bg-gray-100 rounded" />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      <ImageCropModal
        isOpen={!!cropModalState}
        imageSrc={cropModalState?.imageSrc || ''}
        aspect={placementAspectInfo[getEffectivePlacement(formData)].ratio}
        aspectLabel={placementAspectInfo[getEffectivePlacement(formData)].label}
        isSubmitting={uploading}
        onCancel={closeCropModal}
        onConfirm={handleCropConfirm}
      />
    </div>
  );
};

export default BannerForm;
